import React from 'react';
import { X, Loader2, Info } from 'lucide-react';
import { PaymentGatewaySelector } from './PaymentGatewaySelector';

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectPaymentMethod: (methodId: string, gatewayId: string) => void;
  planName: string;
  planPrice: number;
  planCredits: number;
  currencyCode: string;
  currencySymbol: string;
  regionCode: string;
  planId?: string;
  isProcessing?: boolean;
}

export const PaymentModal: React.FC<PaymentModalProps> = ({
  isOpen,
  onClose,
  onSelectPaymentMethod,
  planName,
  planPrice,
  planCredits,
  currencyCode,
  currencySymbol,
  regionCode,
  planId,
  isProcessing = false
}) => {
  // Format price with commas for thousands
  const formatPrice = (price: number): string => {
    return Math.floor(price / 100).toLocaleString();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="min-h-screen px-4 text-center">
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm" 
          onClick={onClose}
        />
        
        <div className="inline-block w-full max-w-md my-8 text-left align-middle transition-all transform">
          <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl">
            <button
              onClick={onClose}
              className="absolute right-4 top-4 p-2 rounded-full text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              disabled={isProcessing}
            >
              <X className="w-5 h-5" />
            </button>

            <div className="p-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Complete Your Purchase
              </h2>
              
              <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="text-gray-700 dark:text-gray-300">Plan:</span>
                  <span className="font-medium text-gray-900 dark:text-white">{planName}</span>
                </div>
                <div className="flex justify-between items-center mt-2">
                  <span className="text-gray-700 dark:text-gray-300">Credits:</span>
                  <span className="font-medium text-gray-900 dark:text-white">{planCredits.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center mt-2">
                  <span className="text-gray-700 dark:text-gray-300">Price:</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {currencySymbol}{formatPrice(planPrice)} {currencyCode}
                  </span>
                </div>
              </div>
              
              {isProcessing ? (
                <div className="flex flex-col items-center justify-center py-8">
                  <Loader2 className="w-10 h-10 text-blue-500 animate-spin mb-4" />
                  <p className="text-gray-700 dark:text-gray-300">Processing your payment...</p>
                </div>
              ) : (
                <PaymentGatewaySelector 
                  regionCode={regionCode}
                  onSelect={(methodId, gatewayId) => onSelectPaymentMethod(methodId, gatewayId)}
                  planId={planId}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};